import  helper from './helper.mjs';
import { h } from '../components/element.mjs';
import { cssPrefix } from '../config.mjs'; 
/**
 * Class          : Annotations
 * Author         : Pratheeshkumar N P
 * Created On     : 24-Apr-2025 
 * Other Fn calls :
 * Comments       :
 * ================================================================================================
 *   Last Modified By  ||    Date        ||    Purpose/Reason
 * ================================================================================================
 */
class Annotations {
    constructor() {
        this._ = [];
        this.length = 0;
    } 
    setData(d) {
        this._ = d.map(it => new Annotation(it));
        this.length = this._.length;
    }
    getData() {
        return this._.map(it => it.getData());
    }
    showFields() { 
        const fieldList = document.getElementById("formFieldsList");
        if (fieldList == undefined) return;
        this._.forEach(it => {
            fieldList.appendChild(it.getNodeElement());
        })
    }
    add(d) {
        const annot = new Annotation(d);
        this._.push(annot);
        this.length++;
        return annot;
    }
    push(annot) { 
        this._.push(annot);
        this.length++;
        return annot;
    }
    remove(annot) {
        const index = this.getIndexof(annot);
        if (index !== -1) {
            this._.splice(index, 1);
            this.length--;
        }
    }
    removeAll(annots) {
        annots.each((a) => {
            const index = this.getIndexof(a);
            a.wrapper.remove();
            if (index !== -1) {
                this._.splice(index, 1);
                this.length--;
            }
        }); 
    }
    includes(annot) {
        return this._.includes(annot);
    }
    getCodes() {
        return this._.map(it => it.code);
    }
    getNames() {
        return this._.map(it => it.name);
    }
    empty() {
        this._ = [];
        this.length = 0;
    }
    getNode(i) {
        if (i === undefined) {
            return this._[0];
        } else {
            return this._[i];
        }
    }
    getIndexof(annot) {
        return this._.findIndex(f => f.code === annot.code); 
    }
    getByCode(code) {
        const index = this._.findIndex(f => f.code === code);
        return this.getNode(index);
    }
    getByCodes(codes) {
        return this._.filter(f => codes.includes(f.code));
    }
    removeByKey(code) {
        const index = this._.findIndex(f => f.code === code);
        if (index !== -1) {
            this._.splice(index, 1);
            this.length--;
        }
    }
    alignH(align) {
        const values = this._.map(a => {
            if (align === "left") return Math.round(a.left);
            if (align === "right") return Math.round(a.left + a.width);
            return Math.round(a.left + a.width / 2); 
        });

        const max = Math.max(...values);
        const min = Math.min(...values);
        const avg = values.reduce((sum, v) => sum + v, 0) / values.length;

        this._.forEach(a => {
            const updated = { ...a };
            if (align === "left") updated.left = min;
            else if (align === "right") updated.left = max - a.width;
            else updated.left = avg - a.width / 2; 
            a.update(updated);
            a.wrapper?.updateElement(a);
            a.wrapper?.updatePosition(a);
        });
    }

    alignV(align, tolerance = 0) {
        const values = this._.map(a => {
            if (align === "top") return Math.round(a.top);
            if (align === "bottom") return Math.round(a.top + a.height);
            return Math.round(a.top + a.height / 2);  
        });

        const max = Math.max(...values);
        const min = Math.min(...values);
        const avg = values.reduce((sum, v) => sum + v, 0) / values.length;

        this._.forEach(a => {  
            const updated = { ...a };
            if (align === "top") updated.top = min;
            else if (align === "bottom") updated.top = max - a.height;
            else updated.top = avg - a.height / 2; 
            a.update(updated); 
            a.wrapper?.updateElement(a);
            a.wrapper?.updatePosition(a);
        });
    }

    distributeHorizontally() {
        if (this._.length < 3) return;

        const sorted = [...this._].sort((a, b) => a.left - b.left);
        const first = sorted[0];
        const last = sorted[sorted.length - 1];

        const totalSpace = last.left - first.left;
        const gap = totalSpace / (sorted.length - 1);

        sorted.forEach((a, i) => {
            if (i === 0 || i === sorted.length - 1) return;  
            const updated = { ...a, left: first.left + gap * i };
            a.update(updated);
        });
    }

    distributeVertically() {
        if (this._.length < 3) return;

        const sorted = [...this._].sort((a, b) => a.top - b.top);
        const first = sorted[0];
        const last = sorted[sorted.length - 1];

        const totalSpace = last.top - first.top;
        const gap = totalSpace / (sorted.length - 1);

        sorted.forEach((a, i) => {
            if (i === 0 || i === sorted.length - 1) return;  
            const updated = { ...a, top: first.top + gap * i };
            a.update(updated);
        });
    }
    each(fn) {
        if (typeof fn !== 'function') return;
        this._.forEach((annot, index) => fn(annot, index));
    }

} 
class Annotation {
    constructor({
        id, code, name, type, width = null, height = null, left = 0, top = 0, readOnly = false, isReport = false, label = "",
        required = false, dataFormat = '', registerId = '-1', wrapper, editableVsl = true, editableOff = true, style, validation, maxDataLength, formula
        } = {}) {
        this.id = id;
        this.code = code;
        this.name = name;
        this.label = label;
        this.required = required;
        this.registerId = registerId;
        this.type = type;
        this.top = Math.round(top);
        this.left = Math.round(left);
        this.height = Math.round(height);
        this.width = Math.round(width);
        this.setDefaultSize(type, width, height);
        this.rect = this.setRect(left, top, this.width, this.height); 
        this.readOnly = !!readOnly;
        const toBool = (v) => {
            if (v === true || v === 1) return true;
            if (v === false || v === 0) return false;
            if (v == null || v === '') return false;
            const s = String(v).toLowerCase().trim();
            return s === '1' || s === 'true' || s === 'yes';
        }; 
        this.isReport = toBool(isReport);
        this.editableOff = toBool(editableOff);
        this.editableVsl = toBool(editableVsl); 
        this.value = null; 
        this.hide = false;
        this.node = h('div', `${cssPrefix}-node d-flex p-1 align-items-center gap-1`); 
        this.dataFormat = dataFormat;
        this.maxDataLength = Number.isInteger(maxDataLength) ? maxDataLength : 20;
        this.wrapper = wrapper || {}; 
        this.__type = 'Annotation';
        this.formula = formula;
        if (typeof validation === 'string') {
            this.validation = new Validator();
            this.validation.setData(validation);
        } else {
            this.validation = validation || new Validator();  
        }
        if (typeof style === 'string') {
            this.style = new TextStyle();
            this.style.setData(style);
        } else {
            this.style = style || new TextStyle();
        }
        
        
    }
    //static create(d) {
    //    const annot = new Annotation(d);
    //   
    //    return annot;
    //}
    update(annot) {
        const updateKeys = [
            "name", "type", "maxDataLength", "width", "height", "top", "left", "readOnly", "isReport", "required",
            "editableOff", "editableVsl", "dataFormat", "style", "wrapper", "registerId", "validation"
        ]; 
        const boolKeys = new Set(["readOnly", "isReport", "required", "editableOff", "editableVsl"]);


        for (const key of updateKeys) {
            if (annot[key] !== undefined) {
                const isNumericKey = ["width", "height", "top", "maxDataLength", "left"].includes(key);
                const value = annot[key];

                if (isNumericKey) {
                    this[key] = Number.isFinite(+value) ? Math.round(+value) : 0;
                } else if (boolKeys.has(key)) { 
                    this[key] = (value === true || value === 1 || String(value).toLowerCase().trim() === 'true' || String(value) === '1');
                } else {
                    this[key] = value;
                } 
            }
        }
        if (annot.left !== undefined && annot.top !== undefined && annot.width !== undefined && annot.height !== undefined) {
            this.rect = this.setRect(this.x, this.y, this.width, this.height);
        } 
        this.wrapper = annot.wrapper || this.wrapper;
    }

    setRect(x, y, w, h) {
        return [x, y, x + w, y + h];
    }
    setValue(value) {
        if (!this.readOnly) {
            this.value = value;
        } else {
            console.warn("Field is read-only. Cannot set value.");
        }
    } 
    getData() {
        const { id, code, name, type, width, height, left, top, readOnly, isReport, label, required, registerId, editableOff, editableVsl, dataFormat, maxDataLength, validation, style, formula } = this;
        return {
            id, code, name, type, width, height, left, top, readOnly: !!readOnly, isReport: !!isReport, label, required: !!required, registerId, editableOff: !!editableOff, editableVsl: !!editableVsl, dataFormat, maxDataLength, validation: validation?.getData(), style: style?.getData(), formula
        };
    }
    getNodeElement() {
        const label = h('div', `${cssPrefix}-node-label flex-fill`);
        const action = h('button', `${cssPrefix}-node-action p-1`);
        label.child(this.name);
        this.node.children(label.el, action.el);
        return this.node.el;
    }
    setDefaultSize(t,w,h) {
        const defaults = {
            textarea: { width: 200, height: 50 },
            textbox: { width: 100, height: 14 },
            numeric: { width: 70, height: 14 },
            date: { width: 70, height: 14 },
            checkbox: { width: 12, height: 12 },
            radio: { width: 12, height: 12 },
            button: { width: 50, height: 20 },
            combobox: { width: 100, height: 13 },
            signature: { width: 200, height: 40 },
        };
        const def = defaults[t];
        if (w == null) this.width = def.width;
        if (h == null) this.height = def.height;
    }
     
} 
class Validations {
    constructor() {
        this._ = [];
    }

    add(validator) {
        if (validator instanceof Validator) {
            this.items.push(validator);
        }
    }

    list() {
        return this._;
    }

    clear() {
        this._ = [];
    }

    validate(value) {
        for (const validator of this._) {
            const result = validator.validate(value);
            if (!result[0]) return result;
        }
        return [true];
    }

    getData() {
        return JSON.stringify(this._.map(v => ({
            required: v.required,
            operator: v.operator,
            value: v.value,
            permission: v.permission,
            msg: v.msg,
            mandtry: v.mandtry,
            type: v.type  
        })));
    }

    setData(jsonString) {
        this.clear();
        try {
            const validators = JSON.parse(jsonString);
            if (Array.isArray(validators)) {
                validators.forEach(v => {
                    this.add(new Validator(
                        v.required,
                        v.operator,
                        v.value,
                        v.permission,
                        v.msg,
                        v.mandtry,
                        v.type || null  
                    ));
                });
            }
        } catch (e) {
            console.error("Invalid validator JSON:", e);
        }
    }
}
const rules = {
    phone: /^[1-9]\d{10}$/,
    email: /w+([-+.]w+)*@w+([-.]w+)*.w+([-.]w+)*/,
};

function returnMessage(flag, key, ...arg) {
    let message = '';
    if (!flag) {
        const params = (arg.length === 1 && typeof arg[0] === 'object') ? arg[0] : { value: arg[0] };
        message = getValidationMessage(key, params);
    }
    return [flag, message];
}
function getValidationMessage(type, { value,length }) { 
    switch (type) {
        case 'required': return 'This field is required.';
        case 'maxLength': return `Value exceeds the maximum allowed length ( ${length}).`;
        case 'invalidFormat': return 'Value does not match the required format.';
        case 'equal': return `Value must be equal to ${value}`;
        case 'notEqual': return `Value must not be equal to ${value}`;
        case 'lessThan': return `Value must be less than ${value}`;
        case 'lessThanEqual': return `Value must be less than or equal to ${value}`;
        case 'greaterThan': return `Value must be greater than ${value}`;
        case 'greaterThanEqual': return `Value must be greater than or equal to ${value}`;
        //case 'between': return `Value must be between ${v1} and ${v2}`;
        //case 'notBetween': return `Value must not be between ${v1} and ${v2}`;
        case 'invalidDate': return 'Invalid date format.';
        case 'minDate': return `Date must be on or after ${value}`;
        case 'maxDate': return `Date must be on or before ${value}`;
        case 'invalidEmail': return 'Invalid email format.';
        case 'invalidPhone': return 'Invalid phone number.';
        default: return 'Invalid value.';
    }
}
class Validator {
    // operator: b|nb|eq|neq|lt|lte|gt|gte
    // type: date|number|list|phone|email 
    constructor(type, operator, value, permission, msg, mandtry, minDate, maxDate) { 
        this.value = value;
        this.operator = operator;
        this.permission = permission;
        this.msg = msg; 
        this.mandtry = mandtry;
        this.minDate = minDate;
        this.maxDate = maxDate;
        this.type = type;
    }

    parseValue(v) {
        const { type } = this;
        if (type) {
            if (type === 'date') {
                return new Date(v);
            }
            if (type === 'number') {
                return Number(v);
            }
        } else {
            if (/^[+-]?\d+$/.test(v)) return Number(v);
        }
        return v;
    } 
    setData(jsonString) {
        try {
            if (jsonString) {
            const validators = JSON.parse(jsonString);
                if (validators) {
                    const { operator, value, type, permission, msg, mandtry, minDate, maxDate } = validators;
                    this.type = type;
                    this.operator = operator;
                    this.value = value;
                    this.permission = permission;
                    this.msg = msg;
                    this.mandtry = mandtry;
                    this.minDate = minDate;
                    this.maxDate = maxDate;
                }
            } 
        } catch (e) {
            console.error("Invalid validator JSON:", e);
        }
    }
    getData() {
        const { 
            value,
            operator,
            permission,
            msg,
            mandtry,
            minDate,
            maxDate,
            type
        } = this;
         
        const isValueSet = value !== '' && value !== null && value !== undefined;
        const hasDateLimits = minDate || maxDate;
        const isSpecialType = ['email', 'phone'].includes(type);

        const shouldInclude = isValueSet || hasDateLimits || isSpecialType || msg;

        if (!shouldInclude) return '';

        return JSON.stringify({ 
            value,
            operator,
            permission,
            msg,
            mandtry,
            minDate,
            maxDate,
            type
        });
    }
    equals(other) {

        let flag = this.type === other.type 
            && this.operator === other.operator;
        if (flag) {
            if (Array.isArray(this.value)) {
                flag = helper.arrayEquals(this.value, other.value);
            } else {
                flag = this.value === other.value;
            }
        }
        return flag;
    }
     
    validate(v, { annotType, required, maxlen, format }) {
        const {
            operator, value, type,
            minDate, maxDate
        } = this;

        if (required && /^\s*$/.test(v)) {
            return returnMessage(false, 'required');
        }
        if (/^\s*$/.test(v)) return [true];

        const v1 = this.parseValue(v);

        // Date type validation
        if (annotType === 'date') {
            const parsedDate = new Date(v);
            if (isNaN(parsedDate.getTime())) {
                return returnMessage(false, 'invalidDate');
            }

            if (minDate && parsedDate < new Date(minDate)) {
                return returnMessage(false, 'minDate', { value: minDate });
            }
            if (maxDate && parsedDate > new Date(maxDate)) {
                return returnMessage(false, 'maxDate', { value: minDate });
            }
        }
        if (annotType == 'textbox' || annotType == 'number') {

            // Max length check
            if (maxlen && v.length > maxlen) {
                return returnMessage(false, 'maxLength', { value: maxlen, length: maxlen });
            }
             
            // Email validation
            if (type === 'email') {
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(v)) {
                    return returnMessage(false, 'invalidEmail');
                }
            }

            // Phone number validation (basic)
            if (type === 'phone') {
                const phoneRegex = /^\+?[0-9\s\-()]{7,20}$/;
                if (!phoneRegex.test(v)) {
                    return returnMessage(false, 'invalidPhone');
                }
            }

            // Operator checks (applies to number or date or parsed values)
            if (operator) {
                const valParsed = this.parseValue(value);
                switch (operator) {
                    case 'eq': return returnMessage(v1 === valParsed, 'equal', value);
                    case 'neq': return returnMessage(v1 !== valParsed, 'notEqual', value);
                    case 'lt': return returnMessage(v1 < valParsed, 'lessThan', value);
                    case 'lte': return returnMessage(v1 <= valParsed, 'lessThanEqual', value);
                    case 'gt': return returnMessage(v1 > valParsed, 'greaterThan', value);
                    case 'gte': return returnMessage(v1 >= valParsed, 'greaterThanEqual', value);
                }
            }
        }
        return [true];
    }


    
}
class TextStyle { 
    constructor({
        fontSize = '10',
        fontFamily = 'Arial, sans-serif',
        lineHeight = '1',
        color = '#000000',
    } = {}) {
        this.fontSize = fontSize;
        this.fontFamily = fontFamily;
        this.lineHeight = lineHeight;
        this.color = color;
    }
     
    parseUnitValue(value) {
        if (typeof value === 'number') return value;
        return parseFloat(value) || 0;
    }
     
    applyTo(element, scaleFactor = 1) {
        if (!(element instanceof HTMLElement)) {
            throw new TypeError('applyTo expects an HTMLElement.');
        }

        const scaledSize = this.parseUnitValue(this.fontSize) * scaleFactor;
        //const scaledLineHeight = this.parseUnitValue(this.lineHeight) * scaleFactor;

        element.style.fontSize = `${scaledSize / 16}rem`;
        element.style.fontFamily = this.fontFamily;
        //element.style.lineHeight = scaledLineHeight.toString();
        element.style.color = this.color;
    }

   
    toStyleObject() {
        return {
            fontSize: this.fontSize,
            fontFamily: this.fontFamily,
            lineHeight: this.lineHeight,
            color: this.color,
        };
    }
 
    update(updates = {}) {
        if (updates.fontSize !== undefined) {
            updates.fontSize = this.parseUnitValue(updates.fontSize);
        }
        if (updates.lineHeight !== undefined) {
            updates.lineHeight = this.parseUnitValue(updates.lineHeight);
        }
        Object.assign(this, updates);
        return this;
    }
     
    clone() {
        return new TextStyle(this.toStyleObject());
    }
     
    toCssText() {
        return Object.entries(this.toStyleObject())
            .map(([key, value]) => `${this.toKebabCase(key)}: ${value}`)
            .join('; ');
    }
     
    toKebabCase(str) {
        return str.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
    }
 
    getData() {
        return JSON.stringify(this.toStyleObject());
    }
     
    setData(jsonString) {
        try {
            const obj = JSON.parse(jsonString);
            return this.update(obj);
        } catch (e) {
            console.error('Invalid JSON for TextStyle:', e);
            return this;
        }
    }
}
class Table {
    constructor(col = 1, row = 1, colW = 70, rowH = 18, padding = 2) {
        this.columns = col;
        this.rows = row;
        this.columnWidth = colW;
        this.rowHeight = rowH;
        this.padding = padding;
        this.left = 100;
        this.top = 100;
        this.type = "table"; 
        this.height = rowH * row;
        this.width = colW * col;
    }
    update() {

    }
}
class TableSettings {
    constructor() {
        this.modal = h('div', 'd-flex flex-column gap-2 ');
        this.header = h('div', 'd-flex gap-2 align-items-center');
        this.editorWrap = h('div', 'd-flex flex-column gap-2 flex-fill overflowy-scroll'); 
        this.fieldName = new FormInput('fieldName', 'Name');
    }
}
export default {};
export {
    Annotations,
    Table,
    TextStyle,
    Validator
};

